import * as React$1 from "react";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { deepEqual } from "fast-equals";
//#region src/Formex.tsx
var FormexContext = React.createContext(null);
var useFormex = () => {
	const ctx = useContext(FormexContext);
	if (!ctx) throw new Error("useFormex must be used within a Formex provider");
	return ctx;
};
var Formex = ({ value, children }) => {
	return /* @__PURE__ */ jsx(FormexContext.Provider, {
		value,
		children
	});
};
//#endregion
//#region src/utils.ts
/** @private is the value an empty array? */
var isEmptyArray = (value) => Array.isArray(value) && value.length === 0;
/** @private is the given object a Function? */
var isFunction = (obj) => typeof obj === "function";
/** @private is the given object an Object? */
var isObject = (obj) => obj !== null && typeof obj === "object";
/** @private is the given object an integer? */
var isInteger = (obj) => String(Math.floor(Number(obj))) === obj;
/** @private is the given object a NaN? */
var isNaN = (obj) => obj !== obj;
/**
* Deeply get a value from an object via its path.
*/
function getIn(obj, key, def, p = 0) {
	if (pathTraversesPrototype(key)) return def;
	const path = toPath(key);
	let current = obj;
	while (current && p < path.length) current = current[path[p++]];
	if (p !== path.length && !current) return def;
	return current === void 0 ? def : current;
}
function setIn(obj, path, value) {
	if (pathTraversesPrototype(path)) return obj;
	const res = clone(obj);
	let resVal = res;
	let i = 0;
	const pathArray = toPath(path);
	for (; i < pathArray.length - 1; i++) {
		const currentPath = pathArray[i];
		const currentObj = getIn(obj, pathArray.slice(0, i + 1));
		if (currentObj && (isObject(currentObj) || Array.isArray(currentObj))) resVal = resVal[currentPath] = clone(currentObj);
		else {
			const nextPath = pathArray[i + 1];
			resVal = resVal[currentPath] = isInteger(nextPath) && Number(nextPath) >= 0 ? [] : {};
		}
	}
	if ((i === 0 ? obj : resVal)[pathArray[i]] === value) return obj;
	if (value === void 0) delete resVal[pathArray[i]];
	else resVal[pathArray[i]] = value;
	if (i === 0 && value === void 0) delete res[pathArray[i]];
	return res;
}
function clone(value) {
	if (Array.isArray(value)) return [...value];
	else if (typeof value === "object" && value !== null) {
		if (Object.getPrototypeOf(value) !== Object.prototype) return value;
		return { ...value };
	} else return value;
}
/**
* Segments that reach the prototype chain rather than a property of the object.
*
* `res["__proto__"] = x` is a setter for the object's prototype, not an own
* property, so a path of `__proto__.polluted` wrote straight onto
* `Object.prototype` and gave every object in the process a `polluted`
* property. `constructor.prototype.x` arrived by a second route, and
* `__proto__.0` did it to arrays.
*
* These are paths, and a path here is a property key — which for a map property
* or a column mapped out of an imported CSV is data, not code.
*/
var UNSAFE_PATH_SEGMENTS = /* @__PURE__ */ new Set([
	"__proto__",
	"constructor",
	"prototype"
]);
/** Whether any segment of this path would traverse the prototype chain. */
function pathTraversesPrototype(path) {
	return toPath(path).some((segment) => UNSAFE_PATH_SEGMENTS.has(segment));
}
function toPath(value) {
	if (Array.isArray(value)) return value;
	return value.replace(/\[(\d+)]/g, ".$1").replace(/^\./, "").replace(/\.$/, "").split(".");
}
//#endregion
//#region src/Field.tsx
function Field({ validate, name, children, as: is, className, ...props }) {
	const formex = useFormex();
	const field = getFieldProps({
		name,
		...props
	}, formex);
	if (isFunction(children)) return children({
		field,
		form: formex
	});
	const asElement = is || "input";
	if (typeof asElement === "string") {
		const { innerRef, ...rest } = props;
		return React$1.createElement(asElement, {
			ref: innerRef,
			...field,
			...rest,
			className
		}, children);
	}
	return React$1.createElement(asElement, {
		...field,
		...props,
		className
	}, children);
}
var getFieldProps = (nameOrOptions, formex) => {
	const name = typeof nameOrOptions === "string" ? nameOrOptions : nameOrOptions.name;
	const valueState = getIn(formex.values, name);
	const field = {
		name,
		value: valueState,
		onChange: formex.handleChange,
		onBlur: formex.handleBlur
	};
	if (typeof nameOrOptions !== "string") {
		const { type, value: valueProp, as: is, multiple } = nameOrOptions;
		if (type === "checkbox") if (valueProp === void 0) field.checked = !!valueState;
		else {
			field.checked = !!(Array.isArray(valueState) && ~valueState.indexOf(valueProp));
			field.value = valueProp;
		}
		else if (type === "radio") {
			field.checked = valueState === valueProp;
			field.value = valueProp;
		} else if (is === "select" && multiple) {
			field.value = field.value || [];
			field.multiple = true;
		}
	}
	return field;
};
//#endregion
//#region src/useCreateFormex.tsx
function useCreateFormex({ initialValues, initialModifiedValues, initialErrors, initialDirty, initialTouched, validation, validateOnChange = false, validateOnInitialRender = false, onSubmit, onReset, onValuesChangeDeferred, debugId }) {
	const startValues = initialModifiedValues ?? initialValues;
	const initialValuesRef = useRef(initialValues);
	const valuesRef = useRef(startValues);
	const debugIdRef = useRef(debugId);
	const [values, setValuesInner] = useState(startValues);
	const [touchedState, setTouchedState] = useState(initialTouched ?? {});
	const touchedRef = useRef(touchedState);
	touchedRef.current = touchedState;
	const [errors, setErrors] = useState(initialErrors ?? {});
	const [dirty, setDirty] = useState(initialDirty ?? !deepEqual(initialValues, startValues));
	const [submitCount, setSubmitCount] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isValidating, setIsValidating] = useState(false);
	const [version, setVersion] = useState(0);
	const onValuesChangeRef = useRef(onValuesChangeDeferred);
	onValuesChangeRef.current = onValuesChangeDeferred;
	const debounceTimeoutRef = useRef(void 0);
	const callDebouncedOnValuesChange = useCallback((values) => {
		if (onValuesChangeRef.current) {
			if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
			debounceTimeoutRef.current = setTimeout(() => {
				onValuesChangeRef.current?.(values, controllerRef.current);
			}, 300);
		}
	}, []);
	const historyRef = useRef([{ values: startValues }]);
	const historyIndexRef = useRef(0);
	/**
	* Record a new state, dropping anything that had been undone past it.
	*/
	const pushHistory = useCallback((entry) => {
		const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
		newHistory.push(entry);
		historyRef.current = newHistory;
		historyIndexRef.current = newHistory.length - 1;
	}, []);
	useEffect(() => {
		if (validateOnInitialRender) validate();
	}, []);
	const setValues = useCallback((newValues) => {
		valuesRef.current = newValues;
		setValuesInner(newValues);
		setDirty(!deepEqual(initialValuesRef.current, newValues));
		pushHistory({ values: newValues });
		callDebouncedOnValuesChange(newValues);
	}, [callDebouncedOnValuesChange, pushHistory]);
	const validate = useCallback(async () => {
		setIsValidating(true);
		const validationErrors = await validation?.(valuesRef.current);
		setErrors(validationErrors ?? {});
		setIsValidating(false);
		return validationErrors;
	}, [validation]);
	const setFieldValue = useCallback((key, value, shouldValidate) => {
		const newValues = setIn(valuesRef.current, key, value);
		valuesRef.current = newValues;
		setValuesInner(newValues);
		if (!deepEqual(getIn(initialValuesRef.current, key), value)) setDirty(true);
		if (shouldValidate) validate();
		pushHistory({ values: newValues });
		callDebouncedOnValuesChange(newValues);
	}, [
		validate,
		callDebouncedOnValuesChange,
		pushHistory
	]);
	const setFieldError = useCallback((key, error) => {
		setErrors((prevErrors) => {
			const newErrors = { ...prevErrors };
			if (error) newErrors[key] = error;
			else delete newErrors[key];
			return newErrors;
		});
	}, []);
	const setFieldTouched = useCallback((key, touched, shouldValidate) => {
		setTouchedState((prev) => ({
			...prev,
			[key]: touched
		}));
		if (shouldValidate) validate();
	}, [validate]);
	const handleChange = useCallback((event) => {
		const target = event.target;
		let value;
		if (target.type === "checkbox") value = target.checked;
		else if (target.type === "number") value = target.valueAsNumber;
		else value = target.value;
		const name = target.name;
		setFieldValue(name, value, validateOnChange);
		setFieldTouched(name, true);
	}, [
		setFieldValue,
		setFieldTouched,
		validateOnChange
	]);
	const handleBlur = useCallback((event) => {
		const name = event.target.name;
		setFieldTouched(name, true);
	}, [setFieldTouched]);
	const submit = useCallback(async (e) => {
		e?.preventDefault();
		e?.stopPropagation();
		setIsSubmitting(true);
		setSubmitCount((prev) => prev + 1);
		const validationErrors = await validation?.(valuesRef.current);
		if (validationErrors && Object.keys(validationErrors).length > 0) setErrors(validationErrors);
		else {
			setErrors({});
			await onSubmit?.(valuesRef.current, controllerRef.current);
		}
		setIsSubmitting(false);
		setVersion((prev) => prev + 1);
	}, [onSubmit, validation]);
	const resetForm = useCallback((props) => {
		const { submitCount: submitCountProp, values: valuesProp, errors: errorsProp, touched: touchedProp, undoable } = props ?? {};
		const priorValues = valuesRef.current;
		const priorTouched = touchedRef.current;
		const nextValues = valuesProp ?? initialValuesRef.current;
		const nextTouched = touchedProp ?? initialTouched ?? {};
		valuesRef.current = nextValues;
		initialValuesRef.current = nextValues;
		setValuesInner(nextValues);
		setErrors(errorsProp ?? {});
		setTouchedState(nextTouched);
		setDirty(false);
		setSubmitCount(submitCountProp ?? 0);
		setVersion((prev) => prev + 1);
		onReset?.(controllerRef.current);
		if (undoable) {
			const kept = historyRef.current.slice(0, historyIndexRef.current + 1);
			kept[kept.length - 1] = {
				values: priorValues,
				touched: priorTouched
			};
			kept.push({
				values: nextValues,
				touched: nextTouched,
				boundary: true
			});
			historyRef.current = kept;
			historyIndexRef.current = kept.length - 1;
		} else {
			historyRef.current = [{ values: nextValues }];
			historyIndexRef.current = 0;
		}
	}, [onReset, initialTouched]);
	/**
	* The `initialValues` prop moved: the record this form edits finished
	* loading, or was replaced. That is a **re-baseline**, not a reset.
	*
	* It used to call `resetForm({ values: initialValues })`, which is a reset
	* in both of the ways that matter, and both were wrong here:
	*
	* - it fired `onReset`, which callers reasonably read as "the user
	*   discarded their changes". The admin clears the cache that seeds an
	*   in-flight edit handed over from the side panel there, so a record's own
	*   data arriving deleted the edit the form had just been opened with, and
	*   the form then re-seeded itself from the server — an expanded record
	*   silently lost whatever had been typed into it.
	* - it overwrote `values`, so anything typed while the record was still
	*   loading was thrown away without a word.
	*
	* So: move the baseline, leave the edit alone, and re-judge one against
	* the other. Only an untouched form follows the baseline to its new value.
	*/
	useEffect(() => {
		if (deepEqual(initialValuesRef.current, initialValues)) return;
		const modified = !deepEqual(initialValuesRef.current, valuesRef.current);
		initialValuesRef.current = initialValues;
		if (modified) setDirty(!deepEqual(initialValues, valuesRef.current));
		else {
			valuesRef.current = initialValues;
			setValuesInner(initialValues);
			historyRef.current = [{ values: initialValues }];
			historyIndexRef.current = 0;
			setDirty(false);
		}
		setVersion((prev) => prev + 1);
	}, [initialValues]);
	const stepHistory = useCallback((newIndex) => {
		const from = historyRef.current[historyIndexRef.current];
		const entry = historyRef.current[newIndex];
		const newValues = entry.values;
		setValuesInner(newValues);
		valuesRef.current = newValues;
		historyIndexRef.current = newIndex;
		setDirty(!deepEqual(initialValuesRef.current, newValues));
		if (entry.touched) setTouchedState(entry.touched);
		if (from?.boundary || entry.boundary) setVersion((prev) => prev + 1);
		callDebouncedOnValuesChange(newValues);
	}, [callDebouncedOnValuesChange]);
	const undo = useCallback(() => {
		if (historyIndexRef.current > 0) stepHistory(historyIndexRef.current - 1);
	}, [stepHistory]);
	const redo = useCallback(() => {
		if (historyIndexRef.current < historyRef.current.length - 1) stepHistory(historyIndexRef.current + 1);
	}, [stepHistory]);
	const controllerRef = useRef({});
	const controller = useMemo(() => ({
		values,
		initialValues: initialValuesRef.current,
		handleChange,
		isSubmitting,
		setSubmitting: setIsSubmitting,
		setValues,
		setFieldValue,
		errors,
		setFieldError,
		touched: touchedState,
		setFieldTouched,
		setTouched: setTouchedState,
		dirty,
		setDirty,
		handleSubmit: submit,
		submitCount,
		setSubmitCount,
		handleBlur,
		validate,
		isValidating,
		resetForm,
		version,
		debugId: debugIdRef.current,
		undo,
		redo,
		canUndo: historyIndexRef.current > 0,
		canRedo: historyIndexRef.current < historyRef.current.length - 1
	}), [
		values,
		errors,
		touchedState,
		dirty,
		isSubmitting,
		submitCount,
		isValidating,
		version,
		handleChange,
		handleBlur,
		setValues,
		setFieldValue,
		setFieldTouched,
		setTouchedState,
		setFieldError,
		validate,
		submit,
		resetForm,
		undo,
		redo
	]);
	useEffect(() => {
		controllerRef.current = controller;
	}, [controller]);
	return controller;
}
//#endregion
export { Field, Formex, clone, getIn, isEmptyArray, isFunction, isInteger, isNaN, isObject, pathTraversesPrototype, setIn, useCreateFormex, useFormex };

//# sourceMappingURL=index.es.js.map